<template>
  <div class="page-user-edit">
    <div class="container mt-5">
      <div class="columns is-centered">
        <div class="column is-half">

          <div class="page-header">
            <h1 class="title is-3 mb-2">Edit User</h1>
            <p class="subtitle is-6 has-text-grey">
              Update account details, access level, and account status.
            </p>
          </div>

          <div v-if="loadingUser" class="box has-text-centered loading-card">
            <button class="button is-primary is-loading" disabled>
              Loading
            </button>
          </div>

          <form v-else @submit.prevent="submitForm" class="box edit-card">
            <div
              v-if="errors.length"
              class="notification is-danger is-light"
            >
              <p
                v-for="error in errors"
                :key="error"
                class="mb-1"
              >
                {{ error }}
              </p>
            </div>

            <div class="form-section">
              <h2 class="title is-5 mb-4">Account Details</h2>

              <div class="field">
                <label class="label">First Name</label>
                <div class="control has-icons-left">
                  <input
                    v-model="user.first_name"
                    class="input"
                    type="text"
                    placeholder="Enter first name"
                  >
                  <span class="icon is-small is-left">
                    <i class="fas fa-user"></i>
                  </span>
                </div>
              </div>

              <div class="field">
                <label class="label">Email</label>
                <div class="control has-icons-left">
                  <input
                    v-model="user.email"
                    class="input"
                    type="email"
                    disabled
                  >
                  <span class="icon is-small is-left">
                    <i class="fas fa-envelope"></i>
                  </span>
                </div>
                <p class="help">
                  Email address cannot be changed.
                </p>
              </div>
            </div>

            <div class="form-section">
              <h2 class="title is-5 mb-4">Password</h2>

              <div class="field">
                <label class="label">New Password</label>
                <div class="control has-icons-left">
                  <input
                    v-model="user.password"
                    class="input"
                    type="password"
                    placeholder="Leave blank to keep existing password"
                  >
                  <span class="icon is-small is-left">
                    <i class="fas fa-lock"></i>
                  </span>
                </div>
              </div>

              <div class="field">
                <label class="label">Repeat Password</label>
                <div class="control has-icons-left">
                  <input
                    v-model="user.repeat_password"
                    class="input"
                    type="password"
                    placeholder="Repeat new password"
                  >
                  <span class="icon is-small is-left">
                    <i class="fas fa-lock"></i>
                  </span>
                </div>
              </div>
            </div>

            <div class="form-section">
              <h2 class="title is-5 mb-4">Permissions</h2>

              <label
                class="setting-row"
                :class="{ 'is-disabled': cannotEditStaffStatus }"
              >
                <div>
                  <strong>Admin User</strong>
                  <p class="help mb-0">
                    Allows this user to access admin features.
                  </p>

                  <p
                    v-if="cannotEditStaffStatus"
                    class="help is-info"
                  >
                    You cannot change your own admin status.
                  </p>
                </div>

                <input
                  v-model="user.is_staff"
                  type="checkbox"
                  :disabled="cannotEditStaffStatus"
                >
              </label>

              <label
                class="setting-row"
                :class="{ 'is-disabled': isEditingCurrentUser }"
              >
                <div>
                  <strong>Active User</strong>
                  <p class="help mb-0">
                    Allows this user to log in to the application.
                  </p>

                  <p
                    v-if="isEditingCurrentUser"
                    class="help is-info"
                  >
                    You cannot make your own account inactive.
                  </p>
                </div>

                <input
                  v-model="user.is_active"
                  type="checkbox"
                  :disabled="isEditingCurrentUser"
                >
              </label>
            </div>

            <div class="field is-grouped is-grouped-right mt-5">
              <div class="control">
                <router-link to="/users" class="button is-light">
                  Cancel
                </router-link>
              </div>
              <div class="control">
                <button
                  class="button is-primary"
                  :class="{ 'is-loading': saving }"
                  :disabled="saving"
                >
                  Save Changes
                </button>
              </div>
            </div>

          </form>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'UserEditView',

  data() {
    return {
      loadingUser: true,
      saving: false,
      errors: [],

      user: {
        id: null,
        first_name: '',
        email: '',
        password: '',
        repeat_password: '',
        is_staff: false,
        is_active: true
      }
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.user
    },
    cannotEditStaffStatus() {
      if (!this.currentUser || !this.currentUser.id || !this.user.id) {
        return false
      }
      return Number(this.user.id) === Number(this.currentUser.id)
    },
    isEditingCurrentUser() {
      if (!this.currentUser || !this.currentUser.id || !this.user.id) {
        return false
      }
      return Number(this.user.id) === Number(this.currentUser.id)
    }
  },

  async mounted() {
    await this.initialise()
  },

  methods: {
    async initialise() {
      this.loadingUser = true

      try {
        const userId = this.$route.params.user_id

        const response = await axios.get(`/api/v1/users/${userId}/`)

        this.user = {
          id: response.data.id,
          first_name: response.data.first_name,
          email: response.data.email,
          password: '',
          repeat_password: '',
          is_staff: response.data.is_staff,
          is_active: response.data.is_active
        }

        if (this.isEditingCurrentUser) {
          this.user.is_active = true
        }

      } catch (error) {
        console.error(error)
        this.errors.push('Failed to load user')
      } finally {
        this.loadingUser = false
      }
    },

    async submitForm() {
      this.errors = []

      if (!this.user.first_name) {
        this.errors.push('First name is required')
      }

      // Password validation
      if (this.user.password || this.user.repeat_password) {
        if (this.user.password !== this.user.repeat_password) {
          this.errors.push('Passwords do not match')
        }
      }

      if (this.isEditingCurrentUser && !this.user.is_active) {
        this.errors.push(
          'You cannot make your own account inactive'
        )
      }

      if (this.errors.length) {
        return
      }

      this.saving = true

      try {
        const payload = {
          first_name: this.user.first_name,

          is_staff: this.cannotEditStaffStatus
            ? true
            : this.user.is_staff,

          is_active: this.isEditingCurrentUser
            ? true
            : this.user.is_active
        }

        if (this.user.password) {
          payload.password = this.user.password
        }

        await axios.put(
          `/api/v1/users/${this.user.id}/`,
          payload
        )

        this.user.password = ''
        this.user.repeat_password = ''

        this.$router.push('/users')

      } catch (error) {
        if (error.response) {
            for (const property in error.response.data) {
                this.errors.push(`${property}: ${error.response.data[property]}`)
            }
            console.log(JSON.stringify(error.response.data))
        } else if (error.message) {
            this.errors.push('Failed to update user')
            console.log(JSON.stringify(error))
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.page-user-edit {
  min-height: 100%;
}

.page-header {
  margin-bottom: 1.5rem;
}

.edit-card,
.loading-card {
  border-radius: 12px;
}

.form-section {
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #eeeeee;
}

.form-section:last-of-type {
  margin-bottom: 0;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  padding: 1rem;
  margin-bottom: 0.75rem;

  border: 1px solid #eeeeee;
  border-radius: 10px;

  background: #fafafa;
}

.setting-row.is-disabled {
  opacity: 0.7;
}

.setting-row input {
  margin-top: 0.25rem;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
}

.notification p:last-child {
  margin-bottom: 0 !important;
}

@media screen and (max-width: 768px) {
  .form-actions .button {
    width: 100%;
  }
}
</style>